import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { MyleTableColumn } from "@/Common/Components/Tables/MyleTableColumn";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { PortfolioConsultationRequestsSortOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { FaxConsultationRequestComposite } from "@/ExportableFile/Models/FaxConsultationRequestComposite";
import { IPrintConsultationResponse } from "@/Portfolio/Api/Dto/Responses/IPrintConsultationResponse";
import { ConsultationRequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { MyConsultationRequestsTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class ConsultationRequestInboxHandler extends BaseOperationHandler {
    constructor() {
        super();
        this.RegisterOperation(LocalOperations.Portfolio.MyConsultationRequests_SearchBoxTextChanged, this.Seacrh_TextChanged);
        this.RegisterOperation(LocalOperations.Portfolio.MyConsultationRequests_Reload, this.Reload);

        this.RegisterOperation(LocalOperations.Portfolio.MyConsultationRequests_Search_CurrentPageChange, this.ResultsPageChange);
        this.RegisterOperation(LocalOperations.Portfolio.MyConsultationRequests_SearchSettings_OnResultsPerPageChange, this.ResultsPerPageChange);
        this.RegisterOperation(LocalOperations.Misc.Table_ColumnSortClick, this.SetColumnSort);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.ClosePopups);
        this.RegisterOperation(LocalOperations.Portfolio.ConsultationRequest_CloseFaxResponsePopup, this.ClosePopups);
        this.RegisterOperation(LocalOperations.Portfolio.ConsultationRequest_CloseFaxPopup, this.ClosePopups);
        this.RegisterOperation(LocalOperations.Portfolio.ConsultationRequest_Fax, this.ConsultationSendFax);
        this.RegisterOperation(LocalOperations.Portfolio.MyConsultations_OnPatientClick, this.onPatientClick);
        this.RegisterOperation(LocalOperations.Portfolio.MyConsultationRequests_SendToPortal_IconClick, this.onPortalClick);
        this.RegisterOperation(LocalOperations.Portfolio.MyConsultationRequests_SendToPortal_CancelClick, this.onPortalClose);
        this.RegisterOperation(LocalOperations.Portfolio.MyConsultationRequests_SendToPortal_SyncClick, this.onPortalSyncClick);
        this.RegisterOperation(LocalOperations.Portfolio.MyConsultationRequests_SendToPortal_RevokeClick, this.onPortalRevokeClick);
    }

    public SetColumnSort(request: LocalEvent, store: PortfolioStore): void {
        var tableTarget = request.Get<MyleTableTarget>("target");

        if (tableTarget === MyleTableTarget.Myle_PortfolioMyConsultationRequests_Search_ColumnSortClick) {
            var columnOrderType = request.Get<PortfolioConsultationRequestsSortOrderType>("sortColumn");

            if (store.ConsultationRequestsInboxModel.get("OrderByColumn") && store.ConsultationRequestsInboxModel.get("OrderByColumn") === columnOrderType) {
                if (store.ConsultationRequestsInboxModel.get("OrderByDirection") === SortDirection.ASC)
                    store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("OrderByDirection", SortDirection.DESC);
                else
                    store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("OrderByDirection", SortDirection.ASC);
            }
            else {
                store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("OrderByColumn", columnOrderType);
                store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("OrderByDirection", SortDirection.ASC);
            }

            var column = request.Get<MyleTableColumn>("columnObject");

            if (column) {
                if (column.columnOptions.IsSortActive) {
                    column.ToggleSortDirection();
                }
                column.columnOptions.IsSortActive = true;
            }

            store.PortfolioActions.LoadMyConsultationRequests(store.ConsultationRequestsInboxModel);
        }
    }

    private ClosePopups(request: LocalEvent, store: PortfolioStore): void {
        if (store.ConsultationRequestsInboxModel.get("IsConsultationRequestFaxPopupOpen")) {
            store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("IsConsultationRequestFaxPopupOpen", false);
            sessionStore.hideGlobalOverlay();
        }

        if (store.ConsultationRequestsInboxModel.get("ConsultationRequestFaxResponse")) {
            store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("ConsultationRequestFaxResponse", null);
            sessionStore.hideGlobalOverlay();
        }
    }

    private ResultsPageChange(request: LocalEvent, store: PortfolioStore): void {
        var value = request.Get<number>("Page");
        store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("SelectedPageNumber", value);
        store.PortfolioActions.LoadMyConsultationRequests(store.ConsultationRequestsInboxModel);
    }

    private ResultsPerPageChange(request: LocalEvent, store: PortfolioStore): void {
        store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel
            .set("SelectedPageNumber", 1)
            .set("SelectedPageSizeOption", request.Get<number>("value"));
    }

    private Seacrh_TextChanged(request: LocalEvent, store: PortfolioStore): void {
        store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("SelectedPageNumber", 1);
        store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel.set("GlobalPortfolioSearchText", request.Get<string>("value"));
        store.PortfolioActions.LoadMyConsultationRequests(store.ConsultationRequestsInboxModel);
    }

    private Reload(request: LocalEvent, store: PortfolioStore): void {
        store.PortfolioActions.LoadMyConsultationRequests(store.ConsultationRequestsInboxModel);
    }

    private ConsultationSendFax(request: LocalEvent, store: PortfolioStore): void {
        let consultationRequest = request.Get<ConsultationRequestsInboxItem>("consultationRequest");
        let pdf = request.Get<string>("pdf");
        let doctorName = request.Get<string>("doctorName");
        let doctorIdentifier = request.Get<string>("doctorIdentifier");
        let doctorAddress = request.Get<string>("doctorAddress");
        let doctorPhone = request.Get<string>("doctorPhone");
        let doctorFax = request.Get<string>("doctorFax");

        var faxConsultationRequestComposite = new FaxConsultationRequestComposite();

        faxConsultationRequestComposite.ConsultationRequestId = consultationRequest.ConsultationRequestId;
        faxConsultationRequestComposite.PatientId = store.ConsultationRequestPopupDataModel.get("Patient").IndividualResponse.Contact_id;
        faxConsultationRequestComposite.PdfFileName = pdf;
        faxConsultationRequestComposite.DoctorName = doctorName;
        faxConsultationRequestComposite.DoctorIdentifier = doctorIdentifier;
        faxConsultationRequestComposite.DoctorAddress = doctorAddress;
        faxConsultationRequestComposite.DoctorPhone = doctorPhone;
        faxConsultationRequestComposite.DoctorFax = doctorFax;

        store.PortfolioActions.FaxConsultationRequest(faxConsultationRequestComposite);
    }

    public onPatientClick(request: LocalEvent): void {
        let item = request.Get<MyConsultationRequestsTableItem>("item");

        window.setTimeout(() => {
            let openPatientOperation = new LocalEvent(LocalOperations.Misc.RedirectToTab);

            openPatientOperation.AddItem("value", item.Model.ContactId);
            openPatientOperation.AddItem("target", RedirectTo.Patient);
            openPatientOperation.AddItem("currentLocation", RouterHistory.location);
            openPatientOperation.AddItem("OpenEncounterId", item.Model.EncounterId);
            openPatientOperation.AddItem("encounterEdit", false);

            LocalEventDispatcher.dispatch(openPatientOperation);
        }, 100);
    }

    private onPortalClick(request: LocalEvent, store: PortfolioStore): void {
        const shareableItem = request.Get<IPortalShareableItem>("portalShareableItem");
        const patientContactId = request.Get<string>("patientContactId");
        const portalSyncState = request.Get<string>("patientPortalSyncState");
        const consultationRequet = request.Get<ConsultationRequestsInboxItem>("portalShareableItem");
        const linkedItemType = shareableItem.getPortalLinkedItemType();

        const linkedModel = Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel())
            .set("PatientContactId", patientContactId)
            .set("RegistrationStatus", portalSyncState)
            .set("DefaultSubject", shareableItem.getSharedItem()?.Subject ?? shareableItem.getDefaultSubject())
            .set("LinkedItemMessages", (shareableItem.getSharedItemMessages() || []).toImmutable())
            .set("LinkedItemType", linkedItemType)
            .set("IsSending", false)
            .set("LinkedItemModel", consultationRequet)
            .set("SharedItem", shareableItem.getSharedItem());

        store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel
            .set("PortalLinkedItemModel", linkedModel)
            .set("IsSendToPatientPortalPopupOpen", true);
    }

    private onPortalClose(): void {
        portfolioStore.ConsultationRequestsInboxModel = portfolioStore.ConsultationRequestsInboxModel
            .set("IsSendToPatientPortalPopupOpen", false)
            .set("PortalLinkedItemModel", null);
    }

    private async generateConsultationRequestPdf(consultationRequest: ConsultationRequestsInboxItem): Promise<ClientResponse<IPrintConsultationResponse>> {
        return await Client.postAsync<IPrintConsultationResponse>(ServerOperations.Server.Myle_PrintConsultationRequest, {
            ConsultationRequestId: consultationRequest.ConsultationRequestId,
            WithSignature: true,
            MarkAsPrinted: true
        });
    }

    private async copyPdfToPortal(portalLinkedItem: IPortalLinkedItemModel, consultationRequest: ConsultationRequestsInboxItem, fileName: string): Promise<ClientResponse<GenericResponse>> {
        return await Client.postAsync<GenericResponse>(ServerOperations.Server.Myle_Patient_Portal_CopyPermanentPdfForPortal, {
            PermanentPdfType: portalLinkedItem.get("LinkedItemType"),
            PermanentPdfId: portalLinkedItem.get("LinkedItemModel").getPortalObjectId(),
            FileNameSigned: fileName,
            FileNameUnsigned: fileName,
            EncounterId: consultationRequest.EncounterId
        });
    }

    private async onPortalSyncClick(request: LocalEvent, store: PortfolioStore): Promise<void> {
        const portalLinkedItem = store.ConsultationRequestsInboxModel.get("PortalLinkedItemModel");
        const consultationRequestBeingShared = portalLinkedItem.get("LinkedItemModel") as ConsultationRequestsInboxItem;
        const priority = request.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = request.Get<boolean>("canPatientReplyWithMessage");
        const subject = request.Get<string>("subject");

        const pdfResponse = await this.generateConsultationRequestPdf(consultationRequestBeingShared);

        if (!pdfResponse.isSuccess) {
            sessionStore.showPopup(pdfResponse.errors);
            return;
        }

        const copyToPortalResponse = await this.copyPdfToPortal(portalLinkedItem, consultationRequestBeingShared, pdfResponse.data.FileName);

        if (!copyToPortalResponse.isSuccess) {
            sessionStore.showPopup(copyToPortalResponse.errors);
            return;
        }

        const newMessage = request.Get<string>("messageText");
        const portalMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(portalLinkedItem);

        // create new message if user has entered text in message textbox
        if (newMessage) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(newMessage, priority));
        }

        const response = await Client.postAsync(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, {
            ObjectId: consultationRequestBeingShared.ConsultationRequestId,
            SharedItemType: portalLinkedItem.get("LinkedItemType"),
            Subject: subject,
            SentByContactId: sessionStore.getCurrentEmployee().ContactId,
            PatientContactId: consultationRequestBeingShared.ContactId,
            CreatedByUserId: sessionStore.userId,
            IncludeLinkedItem: request.Get<string>("includeDocument"),
            Messages: portalMessages,
            SharedItemId: portalLinkedItem.get("SharedItem")?.SharedItemId,
            CanPatientReplyWithMessage: canPatientReplyWithMessage
        });

        if (response.isSuccess) {
            this.onPortalClose();
            store.PortfolioActions.LoadMyConsultationRequests(store.ConsultationRequestsInboxModel);
        }
    }

    private async onPortalRevokeClick(request: LocalEvent, store: PortfolioStore): Promise<void> {
        const portalLinkedItem = store.ConsultationRequestsInboxModel.get("PortalLinkedItemModel");
        const linkedItemModel = portalLinkedItem.get("LinkedItemModel");
        const sharedItemId = linkedItemModel.getSharedItemId();

        const requestData = {
            SharedItemId: sharedItemId,
            SharedItemMessageIds: PortalHelper.getSharedMessageIdsToRevoke(portalLinkedItem),
            UserId: sessionStore.userId,
            IncludeLinkedItem: true,
            SharedItemType: portalLinkedItem.get("LinkedItemType"),
            ObjectId: linkedItemModel.getPortalObjectId()
        };

        if (!IsNullOrWhiteSpace(sharedItemId)) {
            const response = await Client.postAsync(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, requestData);

            if (response.isSuccess) {
                this.onPortalClose();
                store.PortfolioActions.LoadMyConsultationRequests(store.ConsultationRequestsInboxModel);
            }
        }
    }
}

export { ConsultationRequestInboxHandler };
